import React from "react";
import { limitNotif } from "helpers/notificationHelper";
import { IconBox } from "components/IconBox";
import { AppMenuLink } from "../AppMenuLink";
import Tooltip from "components/Tooltip";

interface AppMenuItemProps {
  name: string;
  exactLink?: boolean;
  icon: React.ReactNode;
  isCollapsed: boolean;
  link?: string;
  testId?: string;
  total?: number | null;
  tourName?: string;
  type?: "link" | "tab" | "button";
  onClick?: () => void;
  onNavigate?: () => void;
}

export const AppMenuItem: React.FC<AppMenuItemProps> = ({
  name,
  icon,
  link,
  total,
  isCollapsed,
  type = "link",
  onClick,
  testId,
  tourName,
  exactLink = false,
  onNavigate,
}) => {
  const hasBadge = total !== null && total !== undefined;
  const iconColor = isCollapsed ? "#BDD6FF" : "#0045F5";

  const containerClass = `${tourName ?? ""} ${isCollapsed
    ? "menuleft w-full rounded-md hover:bg-white/20"
    : "menuleft group py-1 overflow-hidden hover:bg-utility-blue-light-50 focus:bg-white"
    }`;

  // When collapsed: center icon in the 68px sidebar
  // When expanded: left-aligned with padding
  const iconWrap = (
    <div
      className={
        isCollapsed
          ? "flex w-full items-center justify-center"
          : "flex shrink-0 items-center"
      }
      style={isCollapsed ? { padding: "12px 0" } : { paddingLeft: "24px", marginRight: "16px" }}
    >
      <IconBox icon={icon} color={iconColor} width="24px" height="24px" />
    </div>
  );

  const labelWrap = !isCollapsed ? (
    <div
      className="flex flex-1 items-center justify-between pr-4"
      style={{ fontSize: "16px", lineHeight: "24px" }}
    >
      <span
        className="sidemuchild font-roboto group-hover:text-royal-blue group-focus:text-royal-blue"
        style={{ fontWeight: 500 }}
      >
        {name}
      </span>
      {hasBadge && (
        <div
          className="ml-2 flex items-center justify-center rounded-full border border-quarternary-400 bg-gray-utility-100"
          style={{ fontSize: "12px", padding: "2px 8px", minWidth: "28px", height: "22px" }}
        >
          <span className="font-roboto font-semibold text-secondary-700">
            {limitNotif(total ?? 0)}
          </span>
        </div>
      )}
    </div>
  ) : null;

  const innerContent = (
    <>
      {iconWrap}
      {labelWrap}
    </>
  );

  const wrappedContent = isCollapsed ? (
    <Tooltip label={name} placement="right" shouldWrapChildren={false}>
      <span className="flex w-full items-center">{innerContent}</span>
    </Tooltip>
  ) : (
    <span className="flex w-full items-center">{innerContent}</span>
  );

  if (type === "button" || !link) {
    return (
      <div className={containerClass}>
        <button
          className={`flex w-full items-center ${isCollapsed ? "justify-center" : ""}`}
          onClick={onClick}
          data-testid={testId}
        >
          {wrappedContent}
        </button>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <AppMenuLink
        to={link}
        newTab={type === "tab"}
        exactLink={exactLink}
        testId={testId}
        onNavigate={onNavigate}
      >
        {wrappedContent}
      </AppMenuLink>
    </div>
  );
};
